import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { REHYDRATE } from 'redux-persist'

import { logout } from '~/auth/slices/auth.slice'
import { MAX_LICENSES_WITHOUT_ADMIN } from '~/familyAccount/familyAccounts.constants'
import { RootState } from '~/redux/store'

import { OnboardingExperimentVariant } from '../consts/experiment.const'
import { getOrderedSteps, ORDERED_STEPS } from '../consts/stepOrder'
import { LearnerAnswers, MathFeeling, OnboardingFlowState, OnboardingFlowStep } from '../consts/types'

const EMPTY_LEARNER: LearnerAnswers = { grade: null, whatMatters: [], mathFeeling: null }

const MIN_LEARNER_COUNT = 1
// The family plan's learner seats, the admin's excluded: picking more than it sells would price
// one number and charge another (YourPlan clamps the same way).
const MAX_LEARNER_COUNT = MAX_LICENSES_WITHOUT_ADMIN
const DEFAULT_LEARNER_COUNT = MIN_LEARNER_COUNT

const clampLearnerCount = (count: number): number => Math.min(MAX_LEARNER_COUNT, Math.max(MIN_LEARNER_COUNT, count))

const resizeLearners = (learners: LearnerAnswers[], count: number): LearnerAnswers[] =>
  Array.from({ length: count }, (_, index) => learners[index] || { ...EMPTY_LEARNER })

// The arm a session walks until a page tells it otherwise. Control is the full funnel, so an
// unresolved flag can only ever show a user more than the experiment asks for, never less.
const initialState: OnboardingFlowState = {
  arm: 'control',
  currentStep: OnboardingFlowStep.HOW_MANY_LEARNERS,
  currentLearnerIndex: 0,
  learnerCount: DEFAULT_LEARNER_COUNT,
  learners: resizeLearners([], DEFAULT_LEARNER_COUNT),
}

const slice = createSlice({
  name: 'onboardingFlow',
  initialState,
  reducers: {
    setLearnerCount: (state, action: PayloadAction<number>) => {
      const learnerCount = clampLearnerCount(action.payload)
      return {
        ...state,
        learnerCount,
        // Keep the active learner index within the (possibly shrunk) learner list so per-learner
        // steps never write to a dropped index.
        currentLearnerIndex: Math.min(state.currentLearnerIndex, learnerCount - 1),
        learners: resizeLearners(state.learners, learnerCount),
      }
    },
    setArm: (state, action: PayloadAction<OnboardingExperimentVariant>) => {
      const arm = action.payload
      const steps = getOrderedSteps(arm)
      // A session re-bucketed mid-flow can hold a step the new arm does not have (control's
      // setup-ready, say); left off the list, indexOf(-1) would send goNext back to the first step.
      // Walk *backwards* to the nearest step the arm does have: that one is on the same page or an
      // earlier one, so the funnel never renders a step belonging to a route the user is not on.
      const earlierSurviving = ORDERED_STEPS.slice(0, ORDERED_STEPS.indexOf(state.currentStep)).filter((step) =>
        steps.includes(step),
      )
      const currentStep = steps.includes(state.currentStep)
        ? state.currentStep
        : earlierSurviving[earlierSurviving.length - 1] ?? steps[0]

      return { ...state, arm, currentStep }
    },
    setCurrentStep: (state, action: PayloadAction<OnboardingFlowStep>) => ({
      ...state,
      currentStep: action.payload,
    }),
    setCurrentLearnerIndex: (state, action: PayloadAction<number>) => ({
      ...state,
      currentLearnerIndex: action.payload,
    }),
    setLearnerGrade: (state, action: PayloadAction<{ index: number; grade: string }>) => ({
      ...state,
      learners: state.learners.map((learner, index) =>
        index === action.payload.index ? { ...learner, grade: action.payload.grade } : learner,
      ),
    }),
    toggleWhatMatters: (state, action: PayloadAction<{ index: number; optionId: string }>) => ({
      ...state,
      learners: state.learners.map((learner, index) => {
        if (index !== action.payload.index) return learner
        const isSelected = learner.whatMatters.includes(action.payload.optionId)
        return {
          ...learner,
          whatMatters: isSelected
            ? learner.whatMatters.filter((optionId) => optionId !== action.payload.optionId)
            : [...learner.whatMatters, action.payload.optionId],
        }
      }),
    }),
    setMathFeeling: (state, action: PayloadAction<{ index: number; feeling: MathFeeling | null }>) => ({
      ...state,
      learners: state.learners.map((learner, index) =>
        index === action.payload.index ? { ...learner, mathFeeling: action.payload.feeling } : learner,
      ),
    }),
    // Keeps the arm: the funnel restarts, the experiment bucket does not.
    resetOnboardingFlow: (state) => ({ ...initialState, arm: state.arm }),
  },
  extraReducers: (builder) => {
    builder.addCase(logout, () => initialState)
    builder.addCase(REHYDRATE as any, (state, action: any) => {
      if (action.key === 'onboardingFlow') {
        // Clamped on the way in as well: a session persisted before the cap came down still holds the
        // old count, and nothing else re-runs the reducer that clamps it.
        const rehydrated = { ...state, ...action.payload }
        const learnerCount = clampLearnerCount(rehydrated.learnerCount)

        return { ...rehydrated, learnerCount, learners: resizeLearners(rehydrated.learners, learnerCount) }
      }
      return state
    })
  },
})

export const selectOnboardingFlow = (state: RootState): OnboardingFlowState => state.onboardingFlow
export const selectArm = (state: RootState): OnboardingExperimentVariant => state.onboardingFlow.arm
export const selectCurrentStep = (state: RootState): OnboardingFlowStep => state.onboardingFlow.currentStep
export const selectCurrentLearnerIndex = (state: RootState): number => state.onboardingFlow.currentLearnerIndex
export const selectLearnerCount = (state: RootState): number => state.onboardingFlow.learnerCount
export const selectLearners = (state: RootState): LearnerAnswers[] => state.onboardingFlow.learners
export const selectCurrentLearner = (state: RootState): LearnerAnswers =>
  state.onboardingFlow.learners[state.onboardingFlow.currentLearnerIndex] || { ...EMPTY_LEARNER }

export const {
  setArm: setArmActionCreator,
  setLearnerCount: setLearnerCountActionCreator,
  setCurrentStep: setCurrentStepActionCreator,
  setCurrentLearnerIndex: setCurrentLearnerIndexActionCreator,
  setLearnerGrade: setLearnerGradeActionCreator,
  toggleWhatMatters: toggleWhatMattersActionCreator,
  setMathFeeling: setMathFeelingActionCreator,
  resetOnboardingFlow: resetOnboardingFlowActionCreator,
} = slice.actions

export default slice.reducer
